import React, { useEffect } from "react";
import { FaGithub, FaLinkedinIn } from "react-icons/fa";
import { TypeAnimation } from "react-type-animation";
import { useInView } from "react-intersection-observer";
import { useDispatch, useSelector } from "react-redux";
import { setActiveSection } from "../../reducers/slices/navSlice";
import { motion } from "framer-motion";
import { fadeIn } from "../../utils/motion";

const Home = () => {
    const dispatch = useDispatch();
    const { ref, inView } = useInView({ threshold: 0.5 });
    const { timeOfLastClick } = useSelector((state) => state.activeSec);

    useEffect(() => {
        if (inView && Date.now() - timeOfLastClick > 1000) {
            dispatch(setActiveSection("home"));
        }
    }, [inView]);

    return (
        <section
            ref={ref}
            id="home"
            className="relative min-h-[90vh] md:min-h-screen flex text-left items-center justify-center overflow-hidden pt-24 pb-16"
        >
            {/* Deep Layered Background Gradients */}
            <div className="absolute inset-0 bg-white dark:bg-premium-darker transition-colors duration-500 z-0"></div>

            {/* Ambient Background Elements isolated */}
            <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-accent-primary rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animation-delay-2000 pointer-events-none z-0"></div>
            <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-[128px] opacity-20 animation-delay-4000 pointer-events-none z-0"></div>

            <div className="w-[95%] max-w-[1260px] mx-auto z-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center justify-between">

                {/* Left Text Column */}
                <div className="flex flex-col justify-center items-start text-left">
                 

                    <motion.h1
                        className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 font-Outfit tracking-tight"
                        initial="hidden"
                        animate="show"
                        variants={fadeIn("up", "ease", 0.4, 0.8)}
                    >
                        Hi, I'm <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-primary to-purple-400">
                            Mohd Rizwan
                        </span>
                    </motion.h1>

                    <motion.div
                        className="text-2xl md:text-3xl font-medium text-rich-black-700 dark:text-gray-400 mb-8 h-12 flex items-center justify-start font-Outfit"
                        initial="hidden"
                        animate="show"
                        variants={fadeIn("up", "ease", 0.6, 0.8)}
                    >
                        I build exceptional{" "}
                        <span className="text-accent-primary ml-2 dark:text-purple-400 font-semibold drop-shadow-sm">
                            <TypeAnimation
                                sequence={[
                                    "Web Apps", 2000,
                                    "Mobile Apps", 2000,
                                    "Cloud Systems", 2000,
                                ]}
                                wrapper="span"
                                speed={50}
                                repeat={Infinity}
                            />
                        </span>
                    </motion.div>

                    <motion.p
                        className="text-lg md:text-xl text-rich-black-600 dark:text-gray-400 max-w-2xl mb-12 leading-relaxed"
                        initial="hidden"
                        animate="show"
                        variants={fadeIn("up", "ease", 0.8, 0.8)}
                    >
                        A full-stack developer specializing in scalable web and mobile applications using React, Next.js, React Native, and AWS. I create secure, high-performance, and pixel-perfect digital solutions.
                    </motion.p>

                    <motion.div
                        className="flex flex-wrap items-center gap-6"
                        initial="hidden"
                        animate="show"
                        variants={fadeIn("up", "ease", 1, 0.8)}
                    >
                        <a href="#projects" className="relative flex items-center justify-center gap-2 bg-accent-primary text-white px-8 py-3 rounded-full font-semibold border border-transparent hover:bg-indigo-600 transition-all duration-300">
                            <span className="relative z-10 tracking-wide">View Projects</span>
                        </a>
                        <a href="/Resume27.pdf" download className="relative flex items-center justify-center gap-2 bg-gray-200 dark:bg-white/5 text-gray-800 dark:text-white px-8 py-3 rounded-full font-semibold border border-transparent dark:border-white/10 hover:bg-gray-300 dark:hover:bg-white/10 transition-all duration-300">
                            <span className="relative z-10 tracking-wide">Download CV</span>
                        </a>
                    </motion.div>

                    <motion.div
                        className="mt-16"
                        initial="hidden"
                        animate="show"
                        variants={fadeIn("up", "ease", 1.2, 0.8)}
                    >
                        <div className="flex gap-4">
                            <a href="https://github.com/rizwan0713" target="_blank" rel="noreferrer" className="p-3 rounded-full bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-700 dark:text-white text-xl transition-colors">
                                <FaGithub />
                            </a>
                            <a href="https://linkedin.com/in/mohd-rizwan1" target="_blank" rel="noreferrer" className="p-3 rounded-full bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-700 dark:text-white text-xl transition-colors">
                                <FaLinkedinIn />
                            </a>
                        </div>
                    </motion.div>
                </div>

                {/* Right Video Column //////////*/}
                <motion.div
                    className="w-full h-full flex justify-center lg:justify-end items-center mt-12 md:mt-0"
                    initial="hidden"
                    animate="show"
                    variants={fadeIn("left", "ease", 0.6, 0.8)}
                >
                    <div className="relative w-full max-w-[450px] lg:max-w-[550px] aspect-[4/5] rounded-[2.5rem] overflow-hidden glass shadow-[0_0_60px_rgba(99,102,241,0.3)] flex items-center justify-center border border-white/20">
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="absolute inset-x-0 top-0 w-full h-[115%] object-cover object-top -mt-6"
                        >
                            <source src="/IntroVideo.mp4" type="video/mp4" />
                        </video>
                        <div className="absolute inset-0 bg-transparent z-10 pointer-events-none rounded-[2.5rem] shadow-inner"></div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Home;
